import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const processPayment = async () => {
      try {
        // Get order ID from URL parameters
        const orderId = searchParams.get('order_id');
        const paypalOrderId = searchParams.get('paypal_order_id');
        
        if (!orderId) {
          setError('Order ID not found in URL');
          setProcessing(false);
          return;
        }

        // Mark order as paid in backend
        const response = await axios.post(`${API}/orders/${orderId}/mark-paid`, {
          paypal_order_id: paypalOrderId,
          payment_timestamp: new Date().toISOString()
        });

        if (response.data.success) {
          setOrderDetails({
            order_id: response.data.order_id,
            order_number: response.data.order_number
          });
          
          // Clear the cart from localStorage
          try {
            localStorage.removeItem('cart');
            // Trigger a storage event to update other tabs/windows
            window.dispatchEvent(new Event('storage'));
          } catch (err) {
            console.error('Failed to clear cart:', err);
          }
          
          toast.success('Payment successful! Your order has been confirmed.');
        } else {
          setError('Failed to confirm payment');
        }
      } catch (err) {
        console.error('Payment confirmation error:', err);
        setError(err.response?.data?.detail || 'Failed to confirm payment');
        toast.error('Failed to confirm payment. Please contact support.');
      } finally {
        setProcessing(false);
      }
    };

    processPayment();
  }, [searchParams]);

  if (processing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing Payment...</h2>
          <p className="text-gray-600">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
              >
                Return to Checkout
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-6 rounded-lg font-semibold transition-colors"
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
        <div className="text-center">
          <div className="text-green-500 text-6xl mb-4">✅</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Payment Successful!</h2>
          <p className="text-gray-600 mb-6">Your order has been confirmed and payment received.</p>
          
          {orderDetails && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-800 mb-2">Order Details</h3>
              <p className="text-green-700">
                Order Number: <span className="font-bold">{orderDetails.order_number}</span>
              </p>
              <p className="text-sm text-green-600 mt-2">
                A confirmation email has been sent to your email address.
              </p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={() => navigate('/customer/account')}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
            >
              View My Orders
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-6 rounded-lg font-semibold transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
